// ISDP CUSTOM: DELICATE, additive Home personalization. Sits between the search
// hero and the action-card grid and stays visually subordinate to the hero —
// small, light, understated.
//
// Renders NOTHING for a brand-new user (no recents): the component returns null
// so Home looks EXACTLY as it does today. It is identity-light and client-side
// only — recents come from a localStorage store.
//
// Two subtle signals, all optional:
//   1. Recently viewed — small horizontal card row from the recentActivity store.
//   2. What's new — a single slim line: "N updates since your last visit →",
//      derived from the announcements feed vs. the last-visit marker. Skipped
//      silently if the feed can't be fetched.
//
// Favourites are NOT shown here — the header's starred dropdown is the single
// favourites surface.
import { useEffect, useState } from 'react';
import type { MouseEvent } from 'react';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { homeMessages } from '../../i18n/home';
import { useLink } from '../common';
import { useRecents } from './useRecents';
import { getLastVisitSnapshot } from './recentActivity';
import './HomePersonalized.css';

// Icon per recent surface — kept in the same visual family as the hero search
// result icons.
function iconForRecentType(type: 'doc' | 'catalog'): string {
  return type === 'doc' ? 'core::document' : 'core::tile-menu';
}

// A single compact strip (a titled row of small chips). Rendered only when it has
// at least one item — the parent decides whether the whole section shows.
function Strip({
  title,
  items,
}: {
  title: string;
  items: {
    key: string;
    icon: string;
    label: string;
    href: string;
    onClick: (e: MouseEvent) => void;
  }[];
}) {
  if (items.length === 0) return null;
  return (
    <div className="devhub-home-personal__strip">
      <h2 className="devhub-home-personal__strip-title">{title}</h2>
      <div className="devhub-home-personal__row">
        {items.map(item => (
          <a
            key={item.key}
            className="devhub-home-personal__chip"
            href={item.href}
            onClick={item.onClick}
            title={item.label}
          >
            <odx-icon className="devhub-home-personal__chip-icon" name={item.icon} size="sm" />
            <span className="devhub-home-personal__chip-label">{item.label}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

// Slim "N updates since your last visit →" nudge. Counts announcements dated
// after the last-visit snapshot. Renders nothing (and never blocks) if the feed
// can't be fetched, there's no prior visit, or nothing is newer.
function WhatsNewLine() {
  const link = useLink();
  const config = useApi(configApiRef);
  const { t } = useTranslationRef(homeMessages);
  const [count, setCount] = useState(0);

  useEffect(() => {
    const lastVisit = getLastVisitSnapshot();
    // First-ever visit: nothing to diff against, so no nudge.
    if (lastVisit === null) return undefined;

    let backendUrl: string;
    try {
      backendUrl = config.getString('backend.baseUrl');
    } catch {
      return undefined;
    }

    let cancelled = false;
    fetch(`${backendUrl}/api/announcements`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: Array<{ date?: string }>) => {
        if (cancelled || !Array.isArray(data)) return;
        const n = data.filter(a => {
          if (!a || !a.date) return false;
          const t2 = Date.parse(a.date);
          return Number.isFinite(t2) && t2 > lastVisit;
        }).length;
        setCount(n);
      })
      .catch(() => {
        // Feed unavailable — skip the signal entirely (don't over-build).
      });

    return () => {
      cancelled = true;
    };
  }, [config]);

  if (count <= 0) return null;

  return (
    <a className="devhub-home-personal__whatsnew" {...link('/announcements')}>
      {t('personalized.whatsNew', { n: String(count) })}
      <odx-icon name="core::chevron-right" size="sm" />
    </a>
  );
}

export const HomePersonalized = () => {
  const link = useLink();
  const { t } = useTranslationRef(homeMessages);

  const recents = useRecents();

  const recentItems = recents.map(r => {
    const l = link(r.url);
    return {
      key: r.url,
      icon: iconForRecentType(r.type),
      label: r.title,
      href: l.href,
      onClick: l.onClick,
    };
  });

  // DELICATE contract: brand-new users (no recents) see today's exact Home. The
  // what's-new line is a bonus that only appears alongside the recents strip,
  // never on its own.
  if (recentItems.length === 0) {
    return null;
  }

  return (
    <section className="devhub-home-personal" aria-label={t('personalized.aria')}>
      <Strip title={t('personalized.recentTitle')} items={recentItems} />
      <WhatsNewLine />
    </section>
  );
};
